import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCompletenessMatrix } from "@/hooks/useClaims";
import type { MatrixEvidence } from "@/hooks/useClaims";
import { EVIDENCE_COLORS } from "@/lib/viz-colors";
import { useEvidenceStore } from "@/stores/evidence-store";
import { ROLE_LABELS } from "@/lib/claimRoles";
import { SCORE_TIER_HIGH, SCORE_TIER_MID } from "@/lib/scoreTiers";

/* ── Constants ─────────────────────────────────────────── */

const CLAIM_TYPE_COLORS: Record<string, string> = {
  thesis: "#BA7517",
  supporting: "#1D9E75",
  counter: "#D85A30",
  synthesis: "#7F77DD",
  gap: "#8B5CF6",
};

// Display labels shared with the Evidence Map slide-over (nouns that don't
// collide with relation verbs; "gap" → "Open question").
const CLAIM_TYPE_LABELS = ROLE_LABELS;

const SEVERITY_COLORS: Record<number, { bg: string; text: string }> = {
  2: { bg: "rgba(216,90,48,0.12)", text: "#D85A30" },
  1: { bg: "rgba(186,117,23,0.12)", text: "#BA7517" },
  0: { bg: "rgba(55,138,221,0.12)", text: "#378ADD" },
};

/* ── Helpers ───────────────────────────────────────────── */

function buildCellLookup(
  evidence: MatrixEvidence[],
): Map<string, Map<string, MatrixEvidence>> {
  const map = new Map<string, Map<string, MatrixEvidence>>();
  for (const e of evidence) {
    if (!map.has(e.claim_id)) map.set(e.claim_id, new Map());
    map.get(e.claim_id)!.set(e.paper_id, e);
  }
  return map;
}

function scoreColor(score: number): string {
  if (score >= SCORE_TIER_HIGH) return "#1D9E75";
  if (score >= SCORE_TIER_MID) return "#BA7517";
  return "#D85A30";
}

/* ── Component ─────────────────────────────────────────── */

interface Props {
  conceptId: string;
}

export function CompletenessMatrix({ conceptId }: Props) {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useCompletenessMatrix(conceptId);

  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);

  /* Derived data */
  const cellLookup = useMemo(
    () => (data ? buildCellLookup(data.evidence) : new Map()),
    [data],
  );

  const claimEvidenceCounts = useMemo(() => {
    if (!data) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const e of data.evidence) {
      counts.set(e.claim_id, (counts.get(e.claim_id) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  const maxEvidenceCount = useMemo(() => {
    let max = 0;
    for (const v of claimEvidenceCounts.values()) {
      if (v > max) max = v;
    }
    return max || 1;
  }, [claimEvidenceCounts]);

  const paperClaimCounts = useMemo(() => {
    if (!data) return new Map<string, number>();
    const seen = new Set<string>();
    const counts = new Map<string, number>();
    for (const e of data.evidence) {
      const key = `${e.paper_id}:${e.claim_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        counts.set(e.paper_id, (counts.get(e.paper_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [data]);

  const maxPaperClaimCount = useMemo(() => {
    let max = 0;
    for (const v of paperClaimCounts.values()) {
      if (v > max) max = v;
    }
    return max || 1;
  }, [paperClaimCounts]);

  const issueCount = useMemo(
    () => data?.audit_issues.filter((i) => i.severity >= 1).length ?? 0,
    [data],
  );
  const noteCount = useMemo(
    () => data?.audit_issues.filter((i) => i.severity === 0).length ?? 0,
    [data],
  );

  /* Interaction helpers */
  function cellOpacity(claimId: string, paperId: string): number {
    if (hoveredRow === null && hoveredCol === null) return 1;
    if (hoveredRow === claimId || hoveredCol === paperId) return 1;
    return 0.35;
  }

  function cellScale(claimId: string, paperId: string): string {
    if (hoveredRow === claimId && hoveredCol === paperId)
      return "scale(1.35)";
    return "scale(1)";
  }

  function handleCellEnter(claimId: string, paperId: string) {
    setHoveredRow(claimId);
    setHoveredCol(paperId);
  }

  function handleRowEnter(claimId: string) {
    setHoveredRow(claimId);
    setHoveredCol(null);
  }

  function handleColEnter(paperId: string) {
    setHoveredRow(null);
    setHoveredCol(paperId);
  }

  function handleContainerLeave() {
    setHoveredRow(null);
    setHoveredCol(null);
  }

  function navigateToClaim(claimId: string) {
    useEvidenceStore.getState().selectClaim(claimId);
    void navigate({ to: ".", search: { view: "map" } });
  }

  /* Loading */
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-6 animate-spin text-purple-500" />
          <span className="text-sm text-muted-foreground">
            Loading matrix...
          </span>
        </div>
      </div>
    );
  }

  /* Error */
  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="text-sm text-red-600">
            {(error as Error).message ?? "Couldn't load matrix data."}
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded bg-purple-100 px-3 py-1 text-xs text-purple-700 hover:bg-purple-200"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  /* Empty */
  if (!data || data.claims.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="text-sm text-muted-foreground">
            Create claims in the Map view to see the completeness matrix
          </span>
          <button
            type="button"
            onClick={() => void navigate({ to: ".", search: { view: "map" } })}
            className="rounded bg-purple-100 px-3 py-1 text-xs text-purple-700 hover:bg-purple-200"
          >
            Go to Map
          </button>
        </div>
      </div>
    );
  }

  /* Hovered cell detail */
  const hoveredEvidence =
    hoveredRow && hoveredCol
      ? cellLookup.get(hoveredRow)?.get(hoveredCol) ?? null
      : null;
  const hoveredClaim = hoveredRow
    ? data.claims.find((c) => c.id === hoveredRow)
    : null;
  const hoveredPaper = hoveredCol
    ? data.papers.find((p) => p.paper_id === hoveredCol)
    : null;

  const sc = scoreColor(data.completeness_score);

  return (
    <div
      className="flex h-full flex-col"
      onMouseLeave={handleContainerLeave}
    >
      {/* ── Header ─────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-4 border-b border-border px-4 py-2">
        <span className="text-sm font-semibold text-foreground">
          Argument completeness
        </span>
        <span className="text-xs text-muted-foreground">
          {data.claims.length} claims &middot; {data.papers.length} papers
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${data.completeness_score}%`,
                backgroundColor: sc,
              }}
            />
          </div>
          <span
            className="text-xs font-medium"
            style={{ color: sc }}
          >
            {data.completeness_score}%
          </span>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Matrix area */}
        <div className="flex-1 overflow-x-auto overflow-y-auto">
          <div className="inline-flex flex-col min-w-full">
            {/* Column headers */}
            <div className="flex shrink-0" style={{ paddingLeft: 180 }}>
              {data.papers.map((p) => (
                <div
                  key={p.paper_id}
                  className="flex items-end justify-center"
                  style={{
                    width: 36,
                    height: 88,
                    opacity:
                      hoveredCol === null && hoveredRow === null
                        ? 1
                        : hoveredCol === p.paper_id
                          ? 1
                          : 0.35,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={() => handleColEnter(p.paper_id)}
                  title={p.title}
                >
                  <span
                    className="block text-muted-foreground select-none"
                    style={{
                      fontSize: 10,
                      lineHeight: 1.1,
                      transform: "rotate(-55deg)",
                      transformOrigin: "center bottom",
                      whiteSpace: "nowrap",
                      maxWidth: 70,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {p.short_label}
                  </span>
                </div>
              ))}
              {/* Spacer for row bar column */}
              <div style={{ width: 64 }} />
            </div>

            {/* Claim rows */}
            {data.claims.map((claim) => {
              const evCount = claimEvidenceCounts.get(claim.id) ?? 0;
              const barW = maxEvidenceCount
                ? (evCount / maxEvidenceCount) * 100
                : 0;
              return (
                <div
                  key={claim.id}
                  className="flex items-center"
                  style={{ height: 42 }}
                >
                  {/* Row label */}
                  <div
                    className="flex shrink-0 items-center gap-2 px-3 overflow-hidden"
                    style={{
                      width: 180,
                      opacity:
                        hoveredRow === null
                          ? 1
                          : hoveredRow === claim.id
                            ? 1
                            : 0.5,
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={() => handleRowEnter(claim.id)}
                    title={claim.text}
                  >
                    <span
                      className="shrink-0 rounded px-1.5 py-0.5 text-white"
                      style={{
                        fontSize: 9,
                        lineHeight: 1,
                        backgroundColor:
                          CLAIM_TYPE_COLORS[claim.claim_type] ?? "#888",
                      }}
                    >
                      {CLAIM_TYPE_LABELS[claim.claim_type] ?? claim.claim_type}
                    </span>
                    <span
                      className="truncate text-foreground"
                      style={{ fontSize: 11 }}
                    >
                      {claim.text}
                    </span>
                  </div>

                  {/* Grid cells */}
                  {data.papers.map((paper) => {
                    const ev = cellLookup
                      .get(claim.id)
                      ?.get(paper.paper_id);
                    const op = cellOpacity(claim.id, paper.paper_id);
                    const sc2 = cellScale(claim.id, paper.paper_id);
                    return (
                      <div
                        key={paper.paper_id}
                        className="flex items-center justify-center"
                        style={{
                          width: 36,
                          height: 42,
                          opacity: op,
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={() =>
                          handleCellEnter(claim.id, paper.paper_id)
                        }
                        onClick={
                          ev ? () => navigateToClaim(claim.id) : undefined
                        }
                      >
                        {ev ? (
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 3,
                              backgroundColor:
                                EVIDENCE_COLORS[ev.relation] ?? "#888",
                              transform: sc2,
                              transition: "transform 0.15s",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <div
                            className="border border-border"
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 3,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Row bar */}
                  <div
                    className="flex shrink-0 items-center gap-1.5 px-2"
                    style={{ width: 64 }}
                  >
                    <div
                      className="h-2.5 rounded-sm"
                      style={{
                        width: `${barW}%`,
                        minWidth: evCount > 0 ? 4 : 0,
                        backgroundColor:
                          CLAIM_TYPE_COLORS[claim.claim_type] ?? "#888",
                        opacity: 0.7,
                      }}
                    />
                    <span
                      className="text-muted-foreground"
                      style={{ fontSize: 10 }}
                    >
                      {evCount}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Footer sparklines */}
            <div
              className="flex shrink-0 items-end border-t border-border"
              style={{ paddingLeft: 180, height: 36 }}
            >
              {data.papers.map((p) => {
                const count = paperClaimCounts.get(p.paper_id) ?? 0;
                const h = maxPaperClaimCount
                  ? (count / maxPaperClaimCount) * 22
                  : 0;
                const overRelied = count >= 3;
                return (
                  <div
                    key={p.paper_id}
                    className="flex items-end justify-center"
                    style={{ width: 36, height: 36, paddingBottom: 4 }}
                    title={`${p.short_label}: ${count} claim${count !== 1 ? "s" : ""}`}
                  >
                    <div
                      className="rounded-sm"
                      style={{
                        width: 10,
                        height: Math.max(h, count > 0 ? 2 : 0),
                        backgroundColor: overRelied ? "#BA7517" : "#9CA3AF",
                        opacity: 0.6,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Audit panel ────────────────────────────── */}
        <div className="flex w-56 shrink-0 flex-col border-l border-border">
          {/* Audit header */}
          <div className="shrink-0 border-b border-border px-3 py-2.5">
            <span className="text-sm font-semibold text-foreground">
              Argument audit
            </span>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {issueCount} issue{issueCount !== 1 ? "s" : ""} &middot;{" "}
              {noteCount} note{noteCount !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Issues list */}
          <div className="flex-1 overflow-y-auto px-2 py-1.5">
            {data.audit_issues.length === 0 ? (
              <span className="text-[10px] text-muted-foreground">
                No issues found
              </span>
            ) : (
              data.audit_issues.map((issue, idx) => {
                const colors = SEVERITY_COLORS[issue.severity] ?? SEVERITY_COLORS[0];
                return (
                  <button
                    key={idx}
                    type="button"
                    className="mb-1.5 w-full rounded px-2 py-1.5 text-left text-[11px] leading-snug transition-colors hover:brightness-95"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                    }}
                    onClick={
                      issue.claim_id
                        ? () => navigateToClaim(issue.claim_id!)
                        : undefined
                    }
                    disabled={!issue.claim_id}
                  >
                    <span
                      className="mr-1 inline-block size-1.5 rounded-full"
                      style={{ backgroundColor: colors.text }}
                    />
                    {issue.text}
                  </button>
                );
              })
            )}
          </div>

          {/* Cell detail */}
          <div className="shrink-0 border-t border-border px-3 py-2">
            {hoveredEvidence && hoveredClaim && hoveredPaper ? (
              <div className="space-y-1">
                <div
                  className="text-[10px] font-medium text-foreground truncate"
                  title={hoveredPaper.title}
                >
                  {hoveredPaper.short_label}
                  {hoveredPaper.year ? ` (${hoveredPaper.year})` : ""}
                </div>
                <div
                  className="text-[10px] text-muted-foreground leading-tight"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {hoveredClaim.text}
                </div>
                <span
                  className="inline-block rounded px-1 py-0.5 text-white"
                  style={{
                    fontSize: 8,
                    lineHeight: 1,
                    backgroundColor:
                      EVIDENCE_COLORS[hoveredEvidence.relation] ?? "#888",
                  }}
                >
                  {hoveredEvidence.relation}
                </span>
              </div>
            ) : hoveredClaim ? (
              <div className="space-y-1">
                <span
                  className="inline-block rounded px-1 py-0.5 text-white"
                  style={{
                    fontSize: 8,
                    lineHeight: 1,
                    backgroundColor:
                      CLAIM_TYPE_COLORS[hoveredClaim.claim_type] ?? "#888",
                  }}
                >
                  {CLAIM_TYPE_LABELS[hoveredClaim.claim_type]}
                </span>
                <div
                  className="text-[10px] text-muted-foreground leading-tight"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {hoveredClaim.text}
                </div>
              </div>
            ) : hoveredPaper ? (
              <div className="space-y-1">
                <div
                  className="text-[10px] font-medium text-foreground truncate"
                  title={hoveredPaper.title}
                >
                  {hoveredPaper.short_label}
                  {hoveredPaper.year ? ` (${hoveredPaper.year})` : ""}
                </div>
                <div
                  className="text-[10px] text-muted-foreground leading-tight truncate"
                  title={hoveredPaper.title}
                >
                  {hoveredPaper.title}
                </div>
              </div>
            ) : (
              <span className="text-[10px] text-muted-foreground">
                Hover rows and cells to explore
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Legend bar ──────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-4 border-t border-border bg-muted px-4 py-1.5">
        {(["supports", "challenges", "qualifies"] as const).map((rel) => (
          <span
            key={rel}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span
              className="inline-block size-2 rounded-sm"
              style={{ backgroundColor: EVIDENCE_COLORS[rel] }}
            />
            {rel.charAt(0).toUpperCase() + rel.slice(1)}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="inline-block size-2 rounded-sm border border-border" />
          No evidence
        </span>
      </div>
    </div>
  );
}
